import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { orderFeedbackApi } from '../utils/api';
// Import heroicons pour les icônes
import {
  StarIcon,
  TruckIcon,
  CubeIcon,
  TrashIcon,
  EyeIcon,
  CalendarIcon,
  PhotoIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  FunnelIcon,
  XMarkIcon,
  ExclamationCircleIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { getDeviseActive } from '../utils/devise';

/**
 * Page de gestion des avis clients dans le backoffice
 * Affiche la liste des feedbacks avec filtres et pagination
 */
const OrderFeedbacks = () => {
  // États des données
  const [feedbacks, setFeedbacks] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // États de la pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // États des filtres
  const [productRatingFilter, setProductRatingFilter] = useState('');
  const [deliveryRatingFilter, setDeliveryRatingFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // État pour le modal de détails
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // État pour la confirmation de suppression
  const [feedbackToDelete, setFeedbackToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Charger les feedbacks
  const loadFeedbacks = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const filters = {
        page,
        pageSize,
        ...(productRatingFilter && { productRating: productRatingFilter }),
        ...(deliveryRatingFilter && { deliveryRating: deliveryRatingFilter }),
        ...(dateFrom && { dateFrom }),
        ...(dateTo && { dateTo })
      };

      const response = await orderFeedbackApi.getAll(filters);
      setFeedbacks(response.data.data || []);
      setTotal(response.data.total || 0);
      setTotalPages(response.data.totalPages || 1);
    } catch (err) {
      console.error('Erreur lors du chargement des feedbacks:', err);
      setError('Impossible de charger les avis');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, productRatingFilter, deliveryRatingFilter, dateFrom, dateTo]);

  // Charger les statistiques
  const loadStats = useCallback(async () => {
    try {
      const response = await orderFeedbackApi.getStats();
      setStats(response.data.data);
    } catch (err) {
      console.error('Erreur lors du chargement des statistiques:', err);
    }
  }, []);

  // Charger les données au montage et quand les filtres changent
  useEffect(() => {
    loadFeedbacks();
  }, [loadFeedbacks]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  // Réinitialiser la page quand les filtres changent
  useEffect(() => {
    setPage(1);
  }, [productRatingFilter, deliveryRatingFilter, dateFrom, dateTo]);

  // Supprimer un feedback
  const handleDelete = async () => {
    if (!feedbackToDelete) return;

    try {
      setDeleting(true);
      await orderFeedbackApi.delete(feedbackToDelete);
      setFeedbackToDelete(null);
      // Recharger les données
      loadFeedbacks();
      loadStats();
    } catch (err) {
      console.error('Erreur lors de la suppression:', err);
      alert('Erreur lors de la suppression de l\'avis');
    } finally {
      setDeleting(false);
    }
  };

  // Voir les détails d'un feedback
  const handleViewDetails = async (feedbackId) => {
    try {
      const response = await orderFeedbackApi.getById(feedbackId);
      setSelectedFeedback(response.data.data);
      setShowDetailModal(true);
    } catch (err) {
      console.error('Erreur lors du chargement des détails:', err);
      alert('Erreur lors du chargement des détails');
    }
  };

  // Réinitialiser les filtres
  const resetFilters = () => {
    setProductRatingFilter('');
    setDeliveryRatingFilter('');
    setDateFrom('');
    setDateTo('');
  };

  // Composant pour afficher les étoiles
  const StarIconDisplay = ({ rating, size = 'h-4 w-4' }) => (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <StarIcon
          key={star}
          className={`${size} ${
            star <= rating
              ? 'fill-yellow-400 text-yellow-400'
              : 'text-gray-300'
          }`}
        />
      ))}
    </div>
  );

  // Affichage du chargement
  if (loading && feedbacks.length === 0) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <ArrowPathIcon className="h-8 w-8 animate-spin text-blue-500" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      {/* En-tête */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Avis clients</h1>
        <p className="text-gray-600 mt-1">Consultez et gérez les avis laissés par vos clients</p>
      </div>

      {/* Statistiques */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="min-w-0 bg-white rounded-lg shadow p-4">
            <p className="text-sm text-gray-500">Total des avis</p>
            <p className="text-2xl font-bold text-gray-900">{stats.total_feedbacks}</p>
          </div>
          <div className="min-w-0 bg-white rounded-lg shadow p-4">
            <p className="text-sm text-gray-500 flex items-center gap-1">
              <CubeIcon className="h-4 w-4" /> Note produits
            </p>
            <div className="flex items-center gap-2 mt-1">
              <p className="text-2xl font-bold text-gray-900">
                {Number(stats.avg_product_rating).toFixed(1)}
              </p>
              <StarIconDisplay rating={Math.round(stats.avg_product_rating)} />
            </div>
          </div>
          <div className="min-w-0 bg-white rounded-lg shadow p-4">
            <p className="text-sm text-gray-500 flex items-center gap-1">
              <TruckIcon className="h-4 w-4" /> Note livraison
            </p>
            <div className="flex items-center gap-2 mt-1">
              <p className="text-2xl font-bold text-gray-900">
                {stats.avg_delivery_rating ? Number(stats.avg_delivery_rating).toFixed(1) : '-'}
              </p>
              {stats.avg_delivery_rating && (
                <StarIconDisplay rating={Math.round(stats.avg_delivery_rating)} />
              )}
            </div>
          </div>
          <div className="min-w-0 bg-white rounded-lg shadow p-4">
            <p className="text-sm text-gray-500">Répartition</p>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-1 text-sm">
              {[5, 4, 3, 2, 1].map((r) => (
                <span key={r} className="flex items-center gap-0.5">
                  <span className="font-medium">{r}</span>
                  <StarIcon className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                  <span className="text-gray-500">({stats[`rating_${r}`] || 0})</span>
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Filtres */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 text-gray-700 hover:text-blue-600"
          >
            <FunnelIcon className="h-5 w-5" />
            <span>Filtres</span>
            {(productRatingFilter || deliveryRatingFilter || dateFrom || dateTo) && (
              <span className="bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded-full">
                Actifs
              </span>
            )}
          </button>
          {(productRatingFilter || deliveryRatingFilter || dateFrom || dateTo) && (
            <button
              onClick={resetFilters}
              className="text-sm text-gray-500 hover:text-red-600 flex items-center gap-1"
            >
              <XMarkIcon className="h-4 w-4" />
              Réinitialiser
            </button>
          )}
        </div>

        {showFilters && (
          <div className="p-4 grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Note produit
              </label>
              <select
                value={productRatingFilter}
                onChange={(e) => setProductRatingFilter(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              >
                <option value="">Toutes</option>
                {[5, 4, 3, 2, 1].map((r) => (
                  <option key={r} value={r}>{r} étoile{r > 1 ? 's' : ''}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Note livraison
              </label>
              <select
                value={deliveryRatingFilter}
                onChange={(e) => setDeliveryRatingFilter(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              >
                <option value="">Toutes</option>
                {[5, 4, 3, 2, 1].map((r) => (
                  <option key={r} value={r}>{r} étoile{r > 1 ? 's' : ''}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date début
              </label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date fin
              </label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              />
            </div>
          </div>
        )}
      </div>

      {/* Liste des feedbacks */}
      {error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <ExclamationCircleIcon className="h-5 w-5 text-red-500" />
          <p className="text-red-700">{error}</p>
        </div>
      ) : feedbacks.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <StarIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun avis</h3>
          <p className="text-gray-500">
            Les avis de vos clients apparaîtront ici une fois qu'ils auront noté leurs commandes.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            {/* Sous md : chaque avis devient une carte (thead masqué) ; dès md : tableau classique */}
            <table className="block min-w-full divide-y divide-gray-200 md:table">
              <thead className="hidden bg-gray-50 md:table-header-group">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Client
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Commande
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Note produit
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Note livraison
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="block bg-white divide-y divide-gray-200 md:table-row-group">
                {feedbacks.map((feedback) => (
                  <tr key={feedback.id} className="grid grid-cols-[1fr_auto] items-center gap-x-3 gap-y-2 p-4 hover:bg-gray-50 md:table-row md:p-0">
                    <td className="order-1 min-w-0 md:table-cell md:px-6 md:py-4 md:whitespace-nowrap">
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {feedback.customer_name || feedback.orders?.customers?.name || 'Client'}
                        </p>
                        <p className="text-sm text-gray-500">
                          {feedback.customer_phone || feedback.orders?.customers?.phone || '-'}
                        </p>
                      </div>
                    </td>
                    <td className="order-3 md:table-cell md:px-6 md:py-4 md:whitespace-nowrap">
                      <Link
                        to={`/app/orders/${feedback.order_id}`}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                      >
                        #{feedback.orders?.order_number || feedback.order_id.slice(0, 8)}
                      </Link>
                    </td>
                    <td className="order-5 col-span-2 flex items-center gap-2 md:table-cell md:px-6 md:py-4 md:whitespace-nowrap">
                      <span className="w-20 text-xs text-gray-500 md:hidden">Produit</span>
                      <StarIconDisplay rating={feedback.product_rating} />
                    </td>
                    <td className="order-6 col-span-2 flex items-center gap-2 md:table-cell md:px-6 md:py-4 md:whitespace-nowrap">
                      <span className="w-20 text-xs text-gray-500 md:hidden">Livraison</span>
                      {feedback.delivery_rating ? (
                        <StarIconDisplay rating={feedback.delivery_rating} />
                      ) : (
                        <span className="text-gray-400 text-sm">-</span>
                      )}
                    </td>
                    <td className="order-4 text-right text-sm text-gray-500 md:table-cell md:px-6 md:py-4 md:whitespace-nowrap md:text-left">
                      {new Date(feedback.created_at).toLocaleDateString('fr-FR', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric'
                      })}
                    </td>
                    <td className="order-2 text-right text-sm font-medium md:table-cell md:px-6 md:py-4 md:whitespace-nowrap">
                      <div className="flex items-center justify-end gap-2">
                        {/* Indicateur d'images */}
                        {feedback.order_feedback_images?.length > 0 && (
                          <span className="flex items-center gap-1 text-gray-400">
                            <PhotoIcon className="h-4 w-4" />
                            <span className="text-xs">{feedback.order_feedback_images.length}</span>
                          </span>
                        )}
                        <button
                          onClick={() => handleViewDetails(feedback.id)}
                          className="text-blue-600 hover:text-blue-800 p-1"
                          title="Voir les détails"
                        >
                          <EyeIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => setFeedbackToDelete(feedback.id)}
                          className="text-red-600 hover:text-red-800 p-1"
                          title="Supprimer"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="px-4 py-4 sm:px-6 border-t border-gray-200 flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm text-gray-500">
              {total} avis au total
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className={`p-2 rounded ${
                  page === 1
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <ChevronLeftIcon className="h-5 w-5" />
              </button>
              <span className="text-sm text-gray-600">
                Page {page} sur {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className={`p-2 rounded ${
                  page === totalPages
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <ChevronRightIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de détails */}
      {showDetailModal && selectedFeedback && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-lg font-semibold">Détails de l'avis</h2>
              <button
                onClick={() => setShowDetailModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Informations client */}
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-2">Client</h3>
                <p className="font-medium">{selectedFeedback.customer_name || '-'}</p>
                <p className="text-sm text-gray-500">{selectedFeedback.customer_phone || '-'}</p>
              </div>

              {/* Notes */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2 flex items-center gap-1">
                    <CubeIcon className="h-4 w-4" /> Note produit
                  </h3>
                  <StarIconDisplay rating={selectedFeedback.product_rating} size="h-6 w-6" />
                </div>
                {selectedFeedback.delivery_rating && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-2 flex items-center gap-1">
                      <TruckIcon className="h-4 w-4" /> Note livraison
                    </h3>
                    <StarIconDisplay rating={selectedFeedback.delivery_rating} size="h-6 w-6" />
                  </div>
                )}
              </div>

              {/* Commentaire */}
              {selectedFeedback.comment && (
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Commentaire</h3>
                  <p className="text-gray-700 bg-gray-50 p-3 rounded-lg">
                    {selectedFeedback.comment}
                  </p>
                </div>
              )}

              {/* Images */}
              {selectedFeedback.order_feedback_images?.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Photos</h3>
                  <div className="grid grid-cols-3 gap-3">
                    {selectedFeedback.order_feedback_images.map((img) => (
                      <a
                        key={img.id}
                        href={img.image_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block aspect-square rounded-lg overflow-hidden border border-gray-200 hover:border-blue-500 transition-colors"
                      >
                        <img
                          src={img.image_url}
                          alt="Avis client"
                          className="w-full h-full object-cover"
                        />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Informations de la commande */}
              {selectedFeedback.orders && (
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Commande associée</h3>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <div className="flex items-center justify-between">
                      <Link
                        to={`/app/orders/${selectedFeedback.order_id}`}
                        className="text-blue-600 hover:text-blue-800 font-medium"
                      >
                        #{selectedFeedback.orders.order_number}
                      </Link>
                      <span className="text-gray-500">
                        {Number(selectedFeedback.orders.total_amount).toLocaleString('fr-FR')} {getDeviseActive()}
                      </span>
                    </div>
                    {selectedFeedback.orders.order_items?.length > 0 && (
                      <div className="mt-2 text-sm text-gray-600">
                        {selectedFeedback.orders.order_items.map((item, idx) => (
                          <div key={idx}>
                            {item.quantity}x {item.product_name}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Date */}
              <div className="text-sm text-gray-500 flex items-center gap-1">
                <CalendarIcon className="h-4 w-4" />
                Avis laissé le {new Date(selectedFeedback.created_at).toLocaleDateString('fr-FR', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </div>
            </div>

            <div className="p-6 border-t border-gray-100 flex justify-end gap-3">
              <button
                onClick={() => setShowDetailModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Fermer
              </button>
              <button
                onClick={() => {
                  setShowDetailModal(false);
                  setFeedbackToDelete(selectedFeedback.id);
                }}
                className="px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700 flex items-center gap-2"
              >
                <TrashIcon className="h-4 w-4" />
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmation de suppression */}
      {feedbackToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="text-center">
              <div className="bg-red-100 rounded-full p-3 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <TrashIcon className="h-8 w-8 text-red-600" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">
                Supprimer cet avis ?
              </h2>
              <p className="text-gray-600 mb-6">
                Cette action est irréversible. L'avis et ses images seront définitivement supprimés.
              </p>
              <div className="flex justify-center gap-3">
                <button
                  onClick={() => setFeedbackToDelete(null)}
                  disabled={deleting}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Annuler
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700 flex items-center gap-2"
                >
                  {deleting ? (
                    <>
                      <ArrowPathIcon className="h-4 w-4 animate-spin" />
                      Suppression...
                    </>
                  ) : (
                    <>
                      <TrashIcon className="h-4 w-4" />
                      Supprimer
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderFeedbacks;
